import { Button, Dialog, Input, Select } from '@artosera/ui';
import { CATALOGUE_POLICES } from '@artosera/site-renderer';
import { ImageOff, ImagePlus, X } from 'lucide-react';
import type { CSSProperties, JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { estApiError } from '../../../api/errors.js';
import { useMediasGalerie, useApercuMedia } from '../../../api/medias.js';
import { useModifierSite, type ReglagesSite } from '../../../api/site.js';
import { galerieALaction } from '../../../auth/sujet.js';
import type { GalerieSession } from '../../../api/types.js';
import { Field } from '../../Field.js';
import styles from './Tabs.module.css';

/** Une couleur hexadécimale complète, la seule que le témoin reprend. */
const COULEUR_VALIDE = /^#[0-9a-fA-F]{6}$/;

export interface CharteTabProps {
  galerieSlug: string;
  site: ReglagesSite;
  galerie: GalerieSession;
}

/** Aperçu du logotype choisi (ou du média survolé dans le sélecteur). */
function LogoApercu({
  galerieSlug,
  mediaId,
}: {
  galerieSlug: string;
  mediaId: string | null;
}): JSX.Element {
  const { t } = useTranslation('regie');
  const apercu = useApercuMedia(galerieSlug, mediaId);
  if (!mediaId) {
    return (
      <span className={styles.logoApercu}>
        <ImageOff size={24} strokeWidth={1.5} aria-hidden />
      </span>
    );
  }
  return (
    <span className={styles.logoApercu}>
      {apercu.data ? (
        <img className={styles.logoImage} src={apercu.data} alt={t('regie:site.charte.logotype')} />
      ) : null}
    </span>
  );
}

/**
 * Charte (S6-f) : logotype par dépose via le mécanisme de médias existant
 * (§ 15.6 : `logotypeMediaId` désigne un média EXISTANT de la galerie —
 * aucune cible de dépose dédiée au logo n'est ouverte, § 15.11), couleurs,
 * polices — la liste offerte par l'API, jamais un catalogue local (§ 15.6 :
 * « policesAdmises sort dans la réponse : la régie en fait deux listes
 * déroulantes sans deviner »).
 */
export function CharteTab({ galerieSlug, site, galerie }: CharteTabProps): JSX.Element {
  const { t } = useTranslation(['regie', 'site', 'commun']);
  const modifier = useModifierSite(galerieSlug);
  const medias = useMediasGalerie(galerieSlug);
  const peutEcrire = galerieALaction(galerie, 'site:write');

  const [logotypeMediaId, setLogotypeMediaId] = useState(site.charte.logotypeMediaId);
  const [logotypeHauteur, setLogotypeHauteur] = useState(site.charte.logotypeHauteur ?? 48);
  const [couleurPrincipale, setCouleurPrincipale] = useState(site.charte.couleurPrincipale);
  const [couleurSecondaire, setCouleurSecondaire] = useState(site.charte.couleurSecondaire);
  const [policeTitres, setPoliceTitres] = useState(site.charte.policeTitres);
  const [policeTexte, setPoliceTexte] = useState(site.charte.policeTexte);
  const [selecteurOuvert, setSelecteurOuvert] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [succes, setSucces] = useState(false);

  async function enregistrer(): Promise<void> {
    setErreur(null);
    setSucces(false);
    try {
      await modifier.mutateAsync({
        charte: {
          logotypeMediaId,
          logotypeHauteur,
          couleurPrincipale,
          couleurSecondaire,
          policeTitres,
          policeTexte,
        },
      });
      setSucces(true);
    } catch (error) {
      setErreur(estApiError(error) ? error.message : t('commun:erreurReseau'));
    }
  }

  // Le cartel témoin reprend les couleurs saisies, seulement quand elles
  // sont lisibles (une saisie en cours ne fait pas clignoter l'aperçu).
  const temoin = {
    ...(COULEUR_VALIDE.test(couleurPrincipale) ? { '--charte-principale': couleurPrincipale } : {}),
    ...(COULEUR_VALIDE.test(couleurSecondaire) ? { '--charte-secondaire': couleurSecondaire } : {}),
  } as CSSProperties;

  return (
    <div className={styles.formulaire}>
      <section className={styles.panneau} aria-labelledby="charte-logotype-titre">
        <div className={styles.panneauEntete}>
          <h2 id="charte-logotype-titre" className={styles.sectionTitre}>
            {t('regie:site.charte.logotype')}
          </h2>
        </div>
        <div className={styles.panneauCorps}>
          <div className={styles.logoZone}>
            <LogoApercu galerieSlug={galerieSlug} mediaId={logotypeMediaId} />
            <div className={styles.logoActions}>
              {peutEcrire ? (
                <Button variant="secondary" onClick={() => setSelecteurOuvert(true)}>
                  <ImagePlus size={16} strokeWidth={1.5} aria-hidden />
                  {t('regie:site.charte.choisirLogo')}
                </Button>
              ) : null}
              {peutEcrire && logotypeMediaId ? (
                <Button variant="ghost" onClick={() => setLogotypeMediaId(null)}>
                  <X size={14} strokeWidth={1.5} aria-hidden />
                  {t('regie:site.charte.retirerLogo')}
                </Button>
              ) : null}
              <div className={styles.logoHauteur}>
                <Field label={t('regie:site.charte.logotypeHauteur')} htmlFor="charte-logo-hauteur">
                  <Input
                    id="charte-logo-hauteur"
                    type="number"
                    mono
                    min={1}
                    max={200}
                    disabled={!peutEcrire}
                    value={logotypeHauteur}
                    onChange={(event) => setLogotypeHauteur(Number(event.target.value) || 1)}
                  />
                </Field>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.panneau} aria-labelledby="charte-couleurs-titre">
        <div className={styles.panneauEntete}>
          <h2 id="charte-couleurs-titre" className={styles.sectionTitre}>
            {t('regie:site.charte.couleursTitre')}
          </h2>
        </div>
        <div className={styles.panneauCorps}>
          <div className={styles.nuancier}>
            <div className={styles.nuancierCarte}>
              <Field
                label={t('regie:site.charte.couleurPrincipale')}
                htmlFor="charte-couleur-principale"
              >
                <div className={styles.couleurLigne}>
                  <input
                    id="charte-couleur-principale"
                    type="color"
                    className={styles.couleurEchantillon}
                    disabled={!peutEcrire}
                    value={couleurPrincipale}
                    onChange={(event) => setCouleurPrincipale(event.target.value)}
                  />
                  <Input
                    mono
                    disabled={!peutEcrire}
                    value={couleurPrincipale}
                    onChange={(event) => setCouleurPrincipale(event.target.value)}
                    aria-label={t('regie:site.charte.couleurPrincipale')}
                  />
                </div>
              </Field>
            </div>
            <div className={styles.nuancierCarte}>
              <Field
                label={t('regie:site.charte.couleurSecondaire')}
                htmlFor="charte-couleur-secondaire"
              >
                <div className={styles.couleurLigne}>
                  <input
                    id="charte-couleur-secondaire"
                    type="color"
                    className={styles.couleurEchantillon}
                    disabled={!peutEcrire}
                    value={couleurSecondaire}
                    onChange={(event) => setCouleurSecondaire(event.target.value)}
                  />
                  <Input
                    mono
                    disabled={!peutEcrire}
                    value={couleurSecondaire}
                    onChange={(event) => setCouleurSecondaire(event.target.value)}
                    aria-label={t('regie:site.charte.couleurSecondaire')}
                  />
                </div>
              </Field>
            </div>
            <div className={styles.temoin} style={temoin} aria-hidden="true">
              <span className={styles.temoinOeuvre} />
              <span className={styles.temoinTrait} />
              <span className={styles.temoinTrait} />
              <span className={styles.temoinTrait} />
            </div>
          </div>
        </div>
      </section>

      <section className={styles.panneau} aria-labelledby="charte-polices-titre">
        <div className={styles.panneauEntete}>
          <h2 id="charte-polices-titre" className={styles.sectionTitre}>
            {t('regie:site.charte.policesTitre')}
          </h2>
          <p className={styles.aide}>
            {t('regie:site.charte.policesAide', { theme: t(`site:themes.${site.theme}`) })}
          </p>
        </div>
        <div className={styles.panneauCorps}>
          <div className={styles.grille2}>
            <Field label={t('regie:site.charte.policeTitres')} htmlFor="charte-police-titres">
              <Select
                id="charte-police-titres"
                aria-label={t('regie:site.charte.policeTitres')}
                value={policeTitres}
                onValueChange={(valeur) => setPoliceTitres(valeur as typeof policeTitres)}
                disabled={!peutEcrire}
                options={site.policesAdmises.titres.map((code) => ({
                  value: code,
                  label: CATALOGUE_POLICES[code].famille,
                }))}
              />
            </Field>
            <Field label={t('regie:site.charte.policeTexte')} htmlFor="charte-police-texte">
              <Select
                id="charte-police-texte"
                aria-label={t('regie:site.charte.policeTexte')}
                value={policeTexte}
                onValueChange={(valeur) => setPoliceTexte(valeur as typeof policeTexte)}
                disabled={!peutEcrire}
                options={site.policesAdmises.texte.map((code) => ({
                  value: code,
                  label: CATALOGUE_POLICES[code].famille,
                }))}
              />
            </Field>
          </div>
        </div>
      </section>

      {erreur || succes || peutEcrire ? (
        <div className={styles.barreActions}>
          <div className={styles.barreMessages}>
            {erreur ? (
              <p className={styles.erreur} role="alert">
                {erreur}
              </p>
            ) : null}
            {succes ? (
              <p className={styles.succes} role="status">
                {t('site:messages.enregistre')}
              </p>
            ) : null}
          </div>
          {peutEcrire ? (
            <Button onClick={() => void enregistrer()} loading={modifier.isPending}>
              {t('commun:actions.enregistrer')}
            </Button>
          ) : null}
        </div>
      ) : null}

      <Dialog
        open={selecteurOuvert}
        onOpenChange={setSelecteurOuvert}
        title={t('regie:site.charte.choisirLogo')}
        closeLabel={t('commun:actions.fermer')}
      >
        {medias.data?.length === 0 || !medias.data ? (
          <p className={styles.vide}>{t('regie:site.charte.aucunMedia')}</p>
        ) : (
          <div className={styles.mediaGrille}>
            {medias.data.map((media) => (
              <MediaChoix
                key={media.id}
                galerieSlug={galerieSlug}
                id={media.id}
                selectionne={media.id === logotypeMediaId}
                onChoisir={() => {
                  setLogotypeMediaId(media.id);
                  setSelecteurOuvert(false);
                }}
              />
            ))}
          </div>
        )}
      </Dialog>
    </div>
  );
}

function MediaChoix({
  galerieSlug,
  id,
  selectionne,
  onChoisir,
}: {
  galerieSlug: string;
  id: string;
  selectionne: boolean;
  onChoisir: () => void;
}): JSX.Element {
  const apercu = useApercuMedia(galerieSlug, id);
  return (
    <button
      type="button"
      className={styles.mediaBouton}
      data-selectionne={selectionne ? 'true' : 'false'}
      onClick={onChoisir}
    >
      {apercu.data ? <img src={apercu.data} alt="" /> : null}
    </button>
  );
}
